import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Frame } from './app/Frame.js';
import { DealsPane, PropertiesPane, VisitsPane } from './app/ListDetail.js';
import { AuthProvider, useAuth } from './auth/AuthContext.js';
import { devToolsEnabled } from './lib/dev-tools.js';
import { AgreementScreen } from './screens/deals/AgreementScreen.js';
import { BillingReturnScreen } from './screens/BillingReturnScreen.js';
import { BillingScreen } from './screens/BillingScreen.js';
import { SettingsScreen } from './screens/settings/SettingsScreen.js';
import { BrandScreen } from './screens/BrandScreen.js';
import { BranchScreen } from './screens/branches/BranchScreen.js';
import { BranchesScreen } from './screens/branches/BranchesScreen.js';
import { DealScreen } from './screens/DealScreen.js';
import { DevBillingScreen } from './screens/DevBillingScreen.js';
import { DevInboxScreen } from './screens/DevInboxScreen.js';
import { DevicesScreen } from './screens/DevicesScreen.js';
import { JoinScreen } from './screens/JoinScreen.js';
import { MoneyScreen } from './screens/MoneyScreen.js';
import { NotificationsScreen } from './screens/NotificationsScreen.js';
import { OnboardingScreen } from './screens/OnboardingScreen.js';
import { PartnersScreen } from './screens/PartnersScreen.js';
import { PassbookScreen } from './screens/PassbookScreen.js';
import { ProofScreen } from './screens/ProofScreen.js';
import { PropertyEditScreen } from './screens/properties/PropertyEditScreen.js';
import { PropertyNewScreen } from './screens/properties/PropertyNewScreen.js';
import { PropertyScreen } from './screens/properties/PropertyScreen.js';
import { RenewalsScreen } from './screens/RenewalsScreen.js';
import { SignInScreen } from './screens/SignInScreen.js';
import { TeamScreen } from './screens/team/TeamScreen.js';
import { TodayScreen } from './screens/TodayScreen.js';
import { VisitScreen } from './screens/visits/VisitScreen.js';

// The platform console is for staff, so it is a chunk of its own.
const Admin = lazy(() =>
  import('./screens/admin/AdminScreen.js').then((m) => ({ default: m.AdminScreen })),
);

// The broker guide is long static text, so its pages are chunks of their own too.
const HelpHub = lazy(() =>
  import('./screens/help/HelpHub.js').then((m) => ({ default: m.HelpHub })),
);
const HelpVisual = lazy(() =>
  import('./screens/help/HelpVisual.js').then((m) => ({ default: m.HelpVisual })),
);
const HelpManual = lazy(() =>
  import('./screens/help/HelpManual.js').then((m) => ({ default: m.HelpManual })),
);

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

const devTools = devToolsEnabled({
  DEV: import.meta.env.DEV,
  VITE_DEV_TOOLS: import.meta.env['VITE_DEV_TOOLS'] as string | undefined,
});

/** `/` is Today, or Money for accounts, who have no deals of their own to work. */
function Home() {
  const auth = useAuth();
  return auth.current?.role === 'accounts' ? <MoneyScreen /> : <TodayScreen />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/signin" element={<SignInScreen />} />
            {devTools ? <Route path="/dev/inbox" element={<DevInboxScreen />} /> : null}
            <Route element={<Frame />}>
              <Route path="/" element={<Home />} />
              <Route path="/onboarding" element={<OnboardingScreen />} />
              <Route path="/deals" element={<DealsPane />}>
                <Route path=":id" element={<DealScreen />} />
                <Route path=":id/agreement" element={<AgreementScreen />} />
              </Route>
              <Route path="/notifications" element={<NotificationsScreen />} />
              <Route
                path="/help"
                element={
                  <Suspense fallback={null}>
                    <HelpHub />
                  </Suspense>
                }
              />
              <Route
                path="/help/visual"
                element={
                  <Suspense fallback={null}>
                    <HelpVisual />
                  </Suspense>
                }
              />
              <Route
                path="/help/manual"
                element={
                  <Suspense fallback={null}>
                    <HelpManual />
                  </Suspense>
                }
              />
              <Route path="/account/sessions" element={<DevicesScreen />} />
              <Route path="/brokerage" element={<Navigate to="/" replace />} />
              <Route path="/visits" element={<VisitsPane />}>
                <Route path=":id" element={<VisitScreen />} />
              </Route>
              <Route path="/properties" element={<PropertiesPane />}>
                <Route path="new" element={<PropertyNewScreen />} />
                <Route path=":id" element={<PropertyScreen />} />
                <Route path=":id/edit" element={<PropertyEditScreen />} />
              </Route>
              <Route path="/renewals" element={<RenewalsScreen />} />
              <Route path="/passbook" element={<PassbookScreen />} />
              <Route path="/partners" element={<PartnersScreen />} />
              <Route path="/billing" element={<BillingScreen />} />
              <Route path="/billing/return" element={<BillingReturnScreen />} />
              {devTools ? (
                <Route path="/dev/billing/pay/:ref" element={<DevBillingScreen />} />
              ) : null}
              <Route path="/introductions/:id" element={<ProofScreen />} />
              <Route path="/team" element={<TeamScreen />} />
              <Route path="/branches" element={<BranchesScreen />} />
              <Route path="/branches/:id" element={<BranchScreen />} />
              <Route path="/brand" element={<BrandScreen />} />
              <Route path="/settings" element={<SettingsScreen />} />
            </Route>
            {/* Outside the frame: its sign in redirect must never copy the token into a URL. */}
            <Route path="/join" element={<JoinScreen />} />
            <Route path="/join/:token" element={<JoinScreen />} />
            <Route
              path="/admin"
              element={
                <Suspense fallback={null}>
                  <Admin />
                </Suspense>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
